import React, { useState, useEffect, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import JoinScreen from './components/JoinScreen';
import Lobby from './components/Lobby';
import ModeratorDashboard from './components/ModeratorDashboard';
import PlayerView from './components/PlayerView';
import SpectatorView from './components/SpectatorView';
import SpectatorJoinScreen from './components/SpectatorJoinScreen';

// Check if we're on a spectate URL (new pattern: /spec/CODE with lowercase)
function getSpectateCode() {
  const match = window.location.pathname.match(/^\/spec\/([a-z0-9]+)$/);
  return match ? match[1] : null;
}

const SOCKET_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:4000';

// Player session helpers
function saveSession(data) {
  try { localStorage.setItem('avalon_session', JSON.stringify(data)); } catch (e) {}
}
function loadSession() {
  try { return JSON.parse(localStorage.getItem('avalon_session')); } catch (e) { return null; }
}
function clearSession() {
  try { localStorage.removeItem('avalon_session'); } catch (e) {}
}

// Moderator session helpers (separate from player session)
function saveModSession(data) {
  try { localStorage.setItem('avalon_mod_session', JSON.stringify(data)); } catch (e) {}
}
function loadModSession() {
  try { return JSON.parse(localStorage.getItem('avalon_mod_session')); } catch (e) { return null; }
}
function clearModSession() {
  try { localStorage.removeItem('avalon_mod_session'); } catch (e) {}
}

function App() {
  const socketRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [view, setView] = useState('join'); // join | lobby | game
  const [isModerator, setIsModerator] = useState(false);
  const [playerId, setPlayerId] = useState(null);
  const [roomCode, setRoomCode] = useState('');
  const [spectatorCode, setSpectatorCode] = useState('');
  const [gameState, setGameState] = useState(null);
  const [modState, setModState] = useState(null);
  const [roleInfo, setRoleInfo] = useState(null);
  const [voteResults, setVoteResults] = useState(null);
  const [questResult, setQuestResult] = useState(null);
  const [gameOverInfo, setGameOverInfo] = useState(null);
  const [error, setError] = useState('');
  const [autoReconnecting, setAutoReconnecting] = useState(false);
  const reconnectAttemptedRef = useRef(false);
  const [isSpectator, setIsSpectator] = useState(false);
  const [spectatorState, setSpectatorState] = useState(null);
  const [spectateRoomCode, setSpectateRoomCode] = useState('');
  const [spectatorPendingCode, setSpectatorPendingCode] = useState(null);
  // Persists spectator identity across socket reconnects
  const spectatorInfoRef = useRef(null); // { code, name }

  // Initialize socket
  useEffect(() => {
    const socket = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      auth: {
        screenWidth: window.screen?.width,
        screenHeight: window.screen?.height,
        platform: navigator.platform || 'unknown',
      },
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);

      // Check for spectate URL (uses spectator code, not room code)
      const specCode = getSpectateCode();
      if (specCode) {
        // If we already have spectator info (reconnect), re-register immediately
        if (spectatorInfoRef.current?.code === specCode) {
          socket.emit('game:spectate', {
            spectatorCode: specCode,
            spectatorName: spectatorInfoRef.current.name,
          }, (res) => {
            if (res.success) {
              setIsSpectator(true);
            } else {
              setError(res.error || 'Failed to rejoin as spectator');
            }
          });
          return;
        }
        // First time on this URL: show name entry screen
        setSpectatorPendingCode(specCode);
        return;
      }

      // Auto-reconnect (only on first connect)
      if (!reconnectAttemptedRef.current) {
        reconnectAttemptedRef.current = true;

        // Try moderator session first
        const modSession = loadModSession();
        if (modSession?.modSessionToken && modSession?.roomCode) {
          setAutoReconnecting(true);
          socket.emit('game:rejoinModerator', {
            modSessionToken: modSession.modSessionToken,
          }, (res) => {
            if (res.success) {
              setAutoReconnecting(false);
              setRoomCode(res.roomCode);
              setPlayerId(res.moderatorId);
              setIsModerator(true);
              setSpectatorCode(res.spectatorCode || '');
              setView('lobby');
              return;
            }
            // Mod session invalid, clear it
            clearModSession();

            // Fall through to try player session
            attemptPlayerReconnect(socket);
          });
          return;
        }

        // Try player session
        attemptPlayerReconnect(socket);
      }
    });

    function attemptPlayerReconnect(sock) {
      const session = loadSession();
      if (session?.sessionToken && session?.roomCode) {
        setAutoReconnecting(true);
        sock.emit('game:join', {
          roomCode: session.roomCode,
          playerName: session.playerName,
          sessionToken: session.sessionToken,
        }, (res) => {
          setAutoReconnecting(false);
          if (res.success) {
            setRoomCode(session.roomCode);
            setPlayerId(res.playerId);
            setIsModerator(false);
            setView('lobby');
            saveSession({
              sessionToken: res.sessionToken || session.sessionToken,
              roomCode: session.roomCode,
              playerId: res.playerId,
              playerName: res.playerName || session.playerName,
            });
          } else {
            clearSession();
          }
        });
      } else {
        setAutoReconnecting(false);
      }
    }

    socket.on('disconnect', () => setConnected(false));

    socket.on('game:state', (state) => {
      setGameState(state);
      if (state.phase !== 'lobby' && state.phase !== 'game_over') {
        setView('game');
      }
    });

    socket.on('game:moderatorState', (state) => {
      setModState(state);
    });

    socket.on('game:roleInfo', (info) => {
      setRoleInfo(info);
    });

    socket.on('game:voteResults', (results) => {
      setVoteResults(results);
    });

    socket.on('game:questResult', (result) => {
      setQuestResult(result);
    });

    socket.on('game:spectatorState', (state) => {
      setSpectatorState(state);
    });

    socket.on('game:over', (info) => {
      setGameOverInfo(info);
    });

    socket.on('game:expired', () => {
      setError('Game has expired due to inactivity.');
      clearSession();
      clearModSession();
      resetToJoin();
    });

    socket.on('game:kicked', () => {
      setError('You have been removed from the game.');
      clearSession();
      resetToJoin();
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const resetToJoin = useCallback(() => {
    setView('join');
    setIsModerator(false);
    setPlayerId(null);
    setRoomCode('');
    setSpectatorCode('');
    setGameState(null);
    setModState(null);
    setRoleInfo(null);
    setVoteResults(null);
    setQuestResult(null);
    setGameOverInfo(null);
    setIsSpectator(false);
    setSpectatorState(null);
    setSpectatorPendingCode(null);
    spectatorInfoRef.current = null;
  }, []);

  const emit = useCallback((event, data) => {
    return new Promise((resolve) => {
      const args = [event];
      if (data !== undefined) args.push(data);
      args.push((response) => resolve(response));
      socketRef.current.emit(...args);
    });
  }, []);

  const handleSpectatorJoin = useCallback(async (name) => {
    setError('');
    const res = await emit('game:spectate', {
      spectatorCode: spectatorPendingCode,
      spectatorName: name,
    });
    if (res.success) {
      spectatorInfoRef.current = { code: spectatorPendingCode, name };
      setSpectateRoomCode(spectatorPendingCode);
      setIsSpectator(true);
    } else {
      setError(res.error || 'Failed to join as spectator');
    }
  }, [emit, spectatorPendingCode]);

  const handleCreateGame = useCallback(async () => {
    setError('');
    const res = await emit('game:create');
    if (res.success) {
      setRoomCode(res.roomCode);
      setPlayerId(res.moderatorId);
      setIsModerator(true);
      setSpectatorCode(res.spectatorCode || '');
      setView('lobby');
      // Save moderator session for reconnection
      saveModSession({
        modSessionToken: res.modSessionToken,
        roomCode: res.roomCode,
        moderatorId: res.moderatorId,
      });
    } else {
      setError(res.error || 'Failed to create game');
    }
  }, [emit]);

  const handleJoinGame = useCallback(async (code, playerName) => {
    setError('');
    const savedSession = loadSession();
    const savedId = sessionStorage.getItem(`avalon_player_${code}`);
    const res = await emit('game:join', {
      roomCode: code,
      playerName,
      playerId: savedId || undefined,
      sessionToken: savedSession?.roomCode === code.toUpperCase().trim() ? savedSession.sessionToken : undefined,
    });
    if (res.success) {
      const normalizedCode = code.toUpperCase().trim();
      setRoomCode(normalizedCode);
      setPlayerId(res.playerId);
      setIsModerator(false);
      sessionStorage.setItem(`avalon_player_${normalizedCode}`, res.playerId);
      saveSession({
        sessionToken: res.sessionToken,
        roomCode: normalizedCode,
        playerId: res.playerId,
        playerName: res.playerName || playerName,
      });
      setView('lobby');
    } else {
      setError(res.error || 'Failed to join game');
    }
  }, [emit]);

  const handleRejoinModerator = useCallback(async () => {
    setError('');
    const modSession = loadModSession();
    if (!modSession?.modSessionToken) {
      setError('No moderator session found');
      return;
    }
    const res = await emit('game:rejoinModerator', {
      modSessionToken: modSession.modSessionToken,
    });
    if (res.success) {
      setRoomCode(res.roomCode);
      setPlayerId(res.moderatorId);
      setIsModerator(true);
      setSpectatorCode(res.spectatorCode || '');
      setView('lobby');
    } else {
      clearModSession();
      setError(res.error || 'Failed to rejoin as moderator');
    }
  }, [emit]);

  const handleLeaveGame = useCallback(async () => {
    const res = await emit('game:leave');
    if (res?.success) {
      // For moderator: keep mod session so they can rejoin
      // For player: clear player session
      if (!isModerator) {
        clearSession();
      }
      resetToJoin();
    }
  }, [emit, isModerator, resetToJoin]);

  const clearVoteResults = useCallback(() => setVoteResults(null), []);
  const clearQuestResult = useCallback(() => setQuestResult(null), []);

  const renderContent = () => {
    // Spectator name entry (pending join)
    if (spectatorPendingCode && !isSpectator) {
      return (
        <SpectatorJoinScreen
          onJoin={handleSpectatorJoin}
          error={error}
          setError={setError}
        />
      );
    }

    // Spectator mode (already joined)
    if (isSpectator) {
      return <SpectatorView spectatorState={spectatorState} roomCode={spectateRoomCode} onLeave={handleLeaveGame} />;
    }

    switch (view) {
      case 'join':
        return (
          <JoinScreen
            onCreateGame={handleCreateGame}
            onJoinGame={handleJoinGame}
            onRejoinModerator={handleRejoinModerator}
            error={error}
            setError={setError}
            autoReconnecting={autoReconnecting}
          />
        );
      case 'lobby':
        if (isModerator) {
          return (
            <ModeratorDashboard
              emit={emit}
              gameState={gameState}
              modState={modState}
              playerId={playerId}
              roomCode={roomCode}
              spectatorCode={spectatorCode}
              roleInfo={roleInfo}
              voteResults={voteResults}
              questResult={questResult}
              gameOverInfo={gameOverInfo}
              clearVoteResults={clearVoteResults}
              clearQuestResult={clearQuestResult}
              onNewGame={() => {
                setGameOverInfo(null);
                setVoteResults(null);
                setQuestResult(null);
                setRoleInfo(null);
              }}
              onLeave={handleLeaveGame}
            />
          );
        }
        return (
          <PlayerView
            emit={emit}
            gameState={gameState}
            playerId={playerId}
            roomCode={roomCode}
            roleInfo={roleInfo}
            voteResults={voteResults}
            questResult={questResult}
            gameOverInfo={gameOverInfo}
            clearVoteResults={clearVoteResults}
            clearQuestResult={clearQuestResult}
            onLeave={handleLeaveGame}
            playerName={gameState?.players?.find(p => p.id === playerId)?.name}
          />
        );
      case 'game':
        if (isModerator) {
          return (
            <ModeratorDashboard
              emit={emit}
              gameState={gameState}
              modState={modState}
              playerId={playerId}
              roomCode={roomCode}
              spectatorCode={spectatorCode}
              roleInfo={roleInfo}
              voteResults={voteResults}
              questResult={questResult}
              gameOverInfo={gameOverInfo}
              clearVoteResults={clearVoteResults}
              clearQuestResult={clearQuestResult}
              onNewGame={() => {
                setGameOverInfo(null);
                setVoteResults(null);
                setQuestResult(null);
                setRoleInfo(null);
              }}
              onLeave={handleLeaveGame}
            />
          );
        }
        return (
          <PlayerView
            emit={emit}
            gameState={gameState}
            playerId={playerId}
            roomCode={roomCode}
            roleInfo={roleInfo}
            voteResults={voteResults}
            questResult={questResult}
            gameOverInfo={gameOverInfo}
            clearVoteResults={clearVoteResults}
            clearQuestResult={clearQuestResult}
            onLeave={handleLeaveGame}
            playerName={gameState?.players?.find(p => p.id === playerId)?.name}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="app">
      <div className={`connection-status ${connected ? 'connected' : 'disconnected'}`}>
        Reconnecting...
      </div>
      <header className="app-header">
        <div className="brand">
          <h1>Gibbs Games</h1>
          <div className="tagline">Virtual Social Strategy Games</div>
        </div>
      </header>
      <main className="app-main">
        {renderContent()}
      </main>
      <footer className="app-footer">
        Gibbs Games &middot; The Resistance: Avalon Companion
      </footer>
    </div>
  );
}

export default App;
